import React, { useEffect, useState } from "react";
import axios from "../routers/axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export default function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [dailyAttempts, setDailyAttempts] = useState([]);
  const [statusStats, setStatusStats] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get("/admin/stats/dashboard");
        setStats(res.data.overview);
        setDailyAttempts(res.data.daily_attempts);
        setStatusStats(res.data.attempt_status);
        setRecentAttempts(res.data.recent_attempts);
      } catch (err) {
        console.error("Lỗi tải dashboard:", err);
      }
    };
    fetchDashboard();
  }, []);

  if (!stats) return <div className="text-gray-500">Đang tải thống kê...</div>;

  const cards = [
    { label: "HỌC VIÊN", value: stats.total_users, color: "bg-blue-100 text-blue-700" },
    { label: "BÀI KIỂM TRA", value: stats.total_tests, color: "bg-green-100 text-green-700" },
    { label: "BÀI LÀM", value: stats.total_attempts, color: "bg-yellow-100 text-yellow-700" },
    { label: "CÂU HỎI", value: stats.total_questions, color: "bg-purple-100 text-purple-700" },
    { label: "THÔNG BÁO", value: stats.total_notifications, color: "bg-pink-100 text-pink-700" },
  ];

  return (
    <div className="space-y-10 p-4 sm:p-6">
      {/* --- Cards Section --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((c, i) => (
          <div
            key={i}
            className={`rounded-2xl shadow-md p-6 ${c.color} text-center font-semibold hover:shadow-lg transition`}
          >
            <div className="text-4xl font-extrabold">{c.value}</div>
            <div className="text-sm uppercase tracking-wide mt-2">{c.label}</div>
          </div>
        ))}
      </div>

      {/* --- Line Chart --- */}
      <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold mb-4 text-gray-700">
          Lượt làm bài trong 14 ngày gần nhất
        </h2>

        {dailyAttempts.length === 0 ? (
          <p className="text-gray-500 text-sm">Chưa có dữ liệu lượt làm bài.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={dailyAttempts} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="colorAttempt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366F1" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#A5B4FC" stopOpacity={0.2} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: "#4B5563" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#4B5563" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  borderRadius: "10px",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="url(#colorAttempt)"
                strokeWidth={3}
                dot={{ r: 4, strokeWidth: 2, stroke: "#6366F1", fill: "#fff" }}
                activeDot={{ r: 6, stroke: "#4338CA", fill: "#818CF8" }}
                isAnimationActive={true}
                animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* --- Pie Chart --- */}
      <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold mb-4 text-gray-700">
          Phân bố trạng thái bài làm
        </h2>

        {statusStats.length === 0 ? (
          <p className="text-gray-500 text-sm">Chưa có dữ liệu trạng thái.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <defs>
                <linearGradient id="colorSubmitted" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FCD34D" />
                  <stop offset="100%" stopColor="#FBBF24" />
                </linearGradient>
                <linearGradient id="colorCompleted" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
              </defs>

              <Pie
                data={statusStats}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={110}
                innerRadius={60}
                paddingAngle={4}
                stroke="#fff"
                strokeWidth={2}
                labelLine={false}
                isAnimationActive={true}
                animationBegin={0}
                animationDuration={1200}
                label={({ percent, name }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {statusStats.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={
                      entry.status === "submitted"
                        ? "url(#colorSubmitted)"
                        : "url(#colorCompleted)"
                    }
                  />
                ))}
              </Pie>

              <Tooltip
                formatter={(value, name) => [`${value} bài`, `Trạng thái: ${name}`]}
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  borderRadius: "10px",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              />
              <Legend
                verticalAlign="bottom"
                iconType="circle"
                wrapperStyle={{ paddingTop: 20, fontSize: 13, color: "#374151" }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* --- Recent Activity Table --- */}
      <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold mb-4 text-gray-700">
          Hoạt động gần đây
        </h2>

        {recentAttempts.length === 0 ? (
          <p className="text-gray-500 text-sm">Chưa có hoạt động nào gần đây.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600 uppercase">
                    Học viên
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600 uppercase">
                    Bài kiểm tra
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600 uppercase">
                    Điểm
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600 uppercase">
                    Thời gian nộp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {recentAttempts.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2 text-gray-800 font-medium">
                      {a.student_name}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{a.test_title}</td>
                    <td className="px-4 py-2 font-semibold text-indigo-600">
                      {a.score}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {a.submitted_at
                        ? new Date(a.submitted_at).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
